import { includes, isEmpty, keys, omit, pick } from 'lodash-es';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { FormSpy, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Link from '@mui/material/Link';

import { graphql } from '__generated__';

import type { RootState } from 'store';

import vectorImg from 'img/Vector.svg';

import { AuthActions } from 'common/auth/actions';
import type { UserSecurityQuestions } from 'common/auth/types';
import { Form } from 'common/component/ValidationFormAndField';
import { CIError } from 'common/error/error_util';
import { nonAuthClient } from 'common/graphql/client';
import { usePageStatus } from 'common/hooks/use-page-status';
import { required } from 'common/utils/validations';
import { CISecondaryButton } from 'common/viewcomp/forms/buttons';
import { CITextField } from 'common/viewcomp/forms/fields/text';
import 'module/auth/security_question/security_answers/security_answers.scss';

import LockoutTimeoutDialog from '../../../login/lockout_timeout_dialog';

export interface SecurityAnswerFormProps {
    onAnswerValidated: () => void;
    onQuestionsMissing: () => void;
    onNeedsActivation: () => void;
    onBackToLogin: () => void;
    showLinks: boolean;
    showLockoutTimer: boolean;
    resetPasswordUserId: string | undefined;
    className: string;
}

interface ValidatorCounts {
    question1ValCount: number;
    question2ValCount: number;
    question3ValCount: number;
}

interface SecurityAnswerFormState {
    questions: UserSecurityQuestions;
    validator: ValidatorCounts;
    selectedContext: {
        selectName: string;
        selectCount: number;
        selectedId: string;
        selectedQuestionText: string;
    };
    isLoading: boolean;
    isClick: boolean;
    answer: string;
    isLockOutModal: boolean;
}

const ClearAnswerFieldOnRequest = ({ version }: { version: number }): ReactElement | null => {
    const form = useForm();

    useEffect(() => {
        if (version > 0) {
            form.change('answer', '');
        }
    }, [version, form]);

    return null;
};

export const SecurityAnswerForm = ({
    onAnswerValidated,
    onQuestionsMissing,
    onNeedsActivation,
    onBackToLogin,
    showLinks,
    showLockoutTimer,
    resetPasswordUserId,
    className,
}: SecurityAnswerFormProps): ReactElement => {
    const { t } = useTranslation('auth');
    const pageStatus = usePageStatus();
    const dispatch = useDispatch();

    const forgotUsername = useSelector((state: RootState) => state.auth.forgotCredentials?.username);
    const questions = useSelector((state: RootState) => state.auth.forgotCredentials?.securityQuestions);
    const [state, setState] = useState<SecurityAnswerFormState>({
        questions: questions,
        isLoading: false,
        isClick: false,
        validator: {
            question1ValCount: 3,
            question2ValCount: 3,
            question3ValCount: 3,
        },
        selectedContext: {
            selectName: 'securityQuestion1Text',
            selectedId: questions && questions.securityQuestion1Id,
            selectedQuestionText: questions && questions.securityQuestion1Text,
            selectCount: 3,
        },
        answer: '',
        isLockOutModal: false,
    });
    const [isLock, setLock] = useState({
        locker: false,
        isHideDBtn: false,
    });
    const [hasAnswerError, setHasAnswerError] = useState(false);
    const [requiredValidationEnabled, setRequiredValidationEnabled] = useState(true);
    const [answerClearVersion, setAnswerClearVersion] = useState(0);

    function requestAnswerClear(): void {
        setRequiredValidationEnabled(false);
        setAnswerClearVersion((version) => version + 1);
    }

    function validateAnswer(
        answer: string,
        questionId: string,
        username: string | null | undefined,
        valCount: keyof ValidatorCounts,
        questionIndex: string
    ): void {
        nonAuthClient
            .query({
                query: VALIDATE_SECURITY_ANSWER,
                variables: {
                    username,
                    questionId,
                    answer,
                    userId: resetPasswordUserId,
                },
            })
            .then((response) => {
                dispatch(
                    AuthActions.forgotCredentialsActivationCodeIssued(
                        response.data.validateUserSecurityAnswer.activationCode
                    )
                );
                dispatch(AuthActions.setPasswordResetUserId(response.data.validateUserSecurityAnswer.userId));
                dispatch(
                    AuthActions.loginRequest(
                        response.data.validateUserSecurityAnswer.username,
                        response.data.validateUserSecurityAnswer.temppass
                    )
                );
                setHasAnswerError(false);
                onAnswerValidated();
            })
            .catch((err) => {
                const errText = CIError.fromGraphQLError(err)?.message ?? '';
                const errBool = errText.match(/locked out/g);
                const isDBHider = isEmpty(pick(state.questions, `securityQuestion${Number(questionIndex) + 1}Text`));
                if (state.validator[valCount] - 1 === 0 || errBool) {
                    if (isDBHider) {
                        setLock({
                            ...isLock,
                            locker: true,
                        });
                    } else {
                        const obj = !errBool ? { isHideDBtn: isDBHider } : { locker: true };
                        setLock({
                            ...isLock,
                            ...obj,
                        });
                    }
                    setState({
                        ...state,
                        isLockOutModal: !errBool,
                        validator: {
                            ...state.validator,
                            [valCount]: state.validator[valCount] - 1,
                        },
                        selectedContext: {
                            selectName: `securityQuestion${Number(questionIndex) + 1}Text`,
                            selectedId:
                                questions &&
                                questions[
                                    `securityQuestion${Number(questionIndex) + 1}Id` as keyof UserSecurityQuestions
                                ],
                            selectedQuestionText:
                                questions &&
                                questions[
                                    `securityQuestion${Number(questionIndex) + 1}Text` as keyof UserSecurityQuestions
                                ],
                            selectCount: 3,
                        },
                        isLoading: false,
                        isClick: false,
                        answer: '',
                    });

                    setHasAnswerError(false);
                    requestAnswerClear();
                } else {
                    setState({
                        ...state,
                        validator: {
                            ...state.validator,
                            [valCount]: state.validator[valCount] - 1,
                        },
                        selectedContext: {
                            ...state.selectedContext,
                            selectCount: state.validator[valCount] - 1,
                        },
                        isLoading: false,
                        isClick: false,
                        answer: '',
                    });
                    setLock({
                        ...isLock,
                        isHideDBtn: isDBHider,
                    });
                    setHasAnswerError(true);
                }
            });
    }

    const { selectedQuestionText, selectCount, selectedId, selectName } = state.selectedContext;

    function securityAnswersSubmit(values: { answer: string }): void {
        setState({
            ...state,
            isLoading: true,
            isClick: true,
            answer: values.answer,
        });
    }

    function nextQuestion(): void {
        setState({
            ...state,
            isLoading: true,
        });

        const questionIndex = parseQuestionNumber(selectName);

        nonAuthClient
            .query({
                query: GET_QUESTIONS_BY_USERNAME,
                variables: {
                    username: forgotUsername,
                },
            })
            .then((response) => {
                const newQuestion = omit(response.data.getUserSecurityQuestions, '__typename') as UserSecurityQuestions;
                const currentQuestions = [
                    state.questions.securityQuestion1Text,
                    state.questions.securityQuestion2Text,
                    state.questions.securityQuestion3Text,
                ];

                // Backend may return the same question again; skip updating state in that case.
                if (!includes(currentQuestions, newQuestion.securityQuestion1Text)) {
                    const nextIndex = Number(questionIndex) + 1;
                    setState({
                        ...state,
                        questions: {
                            ...state.questions,
                            [`securityQuestion${nextIndex}Id`]: newQuestion.securityQuestion1Id,
                            [`securityQuestion${nextIndex}Text`]: newQuestion.securityQuestion1Text,
                        },
                        selectedContext: {
                            selectName: `securityQuestion${nextIndex}Text`,
                            selectedId: newQuestion.securityQuestion1Id,
                            selectedQuestionText: newQuestion.securityQuestion1Text,
                            selectCount: 3,
                        },
                    });
                    setHasAnswerError(false);
                }
            })
            .catch((err): void => {
                pageStatus.error(t(CIError.fromGraphQLError(err)?.translationKey));
            })
            .finally(() => {
                setState((state) => ({
                    ...state,
                    isLoading: false,
                }));
            });

        requestAnswerClear();
    }

    function clearStorage(): void {
        dispatch(AuthActions.forgotCredentialsQuestionsDiscarded());
    }

    const { isLoading, isClick, answer } = state;
    useEffect(() => {
        const questionIndex = parseQuestionNumber(selectName);
        const valCount = keys(state.validator)[Number(questionIndex) - 1] as keyof ValidatorCounts;
        if (isClick && answer && valCount) {
            validateAnswer(answer, selectedId, forgotUsername, valCount, questionIndex);
        } else {
            setState({
                ...state,
                isLoading: false,
                isClick: false,
            });
        }
    }, [isClick]);

    useEffect(() => {
        if (isLock.locker) {
            clearStorage();
        }
    }, [isLock.locker]);

    useEffect(() => {
        return (): void => {
            clearStorage();
        };
    }, []);

    useEffect(() => {
        if (state.questions === null) {
            onQuestionsMissing();
        }
    }, [state.questions]);

    const closeLockOutModal = (): void => {
        setState({
            ...state,
            isLockOutModal: false,
        });
    };

    return (
        <div className={`security_answer_container ${className}`}>
            <Form
                onSubmit={securityAnswersSubmit}
                render={({ handleSubmit, invalid }): ReactNode => {
                    const disableButton = invalid || isLock.locker || hasAnswerError;

                    return (
                        <form id="security_answer_form" onSubmit={handleSubmit} noValidate>
                            <ClearAnswerFieldOnRequest version={answerClearVersion} />
                            {!isLock.locker ? (
                                <>
                                    <h1 className="title" id="answer_security_question_header">
                                        {t('security_question.answer_primary_title')}
                                    </h1>
                                    <div className="questionsText">{selectedQuestionText}</div>
                                    <CITextField
                                        t={t}
                                        className={hasAnswerError ? 'ansErr' : ''}
                                        id="answer"
                                        name="answer"
                                        labelI18NKey="security_question.answer_placeholder"
                                        type="text"
                                        margin="normal"
                                        fullWidth
                                        required
                                        helperText={
                                            hasAnswerError &&
                                            t('security_question.answer_error_attempts', { count: selectCount })
                                        }
                                        validations={(requiredValidationEnabled && [required()]) || []}
                                    />
                                    <CISecondaryButton
                                        className={`btn submit_button ${isLoading && 'btn_disabler'}`}
                                        type="submit"
                                        fullWidth
                                        id="security_answer_btn"
                                        disabled={disableButton}
                                        isLoading={isLoading}
                                    >
                                        {t('security_question.submit')}
                                    </CISecondaryButton>
                                    {selectCount < 3 && !isLock.isHideDBtn && (
                                        <CISecondaryButton
                                            className="btn diff_button"
                                            type="button"
                                            fullWidth
                                            id="security_diff_button"
                                            disabled={false}
                                            isLoading={isLoading}
                                            onClick={nextQuestion}
                                        >
                                            {t('security_question.different_question')}
                                        </CISecondaryButton>
                                    )}

                                    <FormSpy
                                        subscription={{
                                            error: true,
                                            valid: true,
                                            values: true,
                                            initialValues: true,
                                            pristine: true,
                                        }}
                                        onChange={(formState): void => {
                                            if (formState.values) {
                                                const { answer } = formState.values;
                                                if (answer !== '') {
                                                    setRequiredValidationEnabled(true);
                                                }
                                            }
                                            setHasAnswerError(false);
                                        }}
                                    />

                                    {showLinks && (
                                        <div className="link_container">
                                            <Link
                                                component="button"
                                                variant="body2"
                                                className="auth-link"
                                                id="dont-know-answer"
                                                onClick={onNeedsActivation}
                                            >
                                                {t('security_question.dont_know_answer')}
                                            </Link>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                className="auth-link"
                                                id="securityBTLogin"
                                                onClick={onBackToLogin}
                                            >
                                                {t('security_question.back_to_login')}
                                            </Link>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="locked_container">
                                        <div className="locker">
                                            <img alt={t('security_question.vector')} src={vectorImg} />
                                        </div>
                                        <div className="lock_text_container">
                                            <p className="lock_text">{t('security_question.locker_one_text')}</p>
                                        </div>
                                        <CISecondaryButton
                                            className="btn"
                                            type="button"
                                            fullWidth
                                            id="security_quest_bt_login"
                                            disabled={false}
                                            onClick={onBackToLogin}
                                        >
                                            {t('security_question.back_to_login')}
                                        </CISecondaryButton>
                                    </div>
                                    {state.isLockOutModal && showLockoutTimer && (
                                        <LockoutTimeoutDialog onClose={closeLockOutModal} isClearTimer={true} t={t} />
                                    )}
                                </>
                            )}
                        </form>
                    );
                }}
            />
        </div>
    );
};

function parseQuestionNumber(selectName: string): string {
    // `selectName` is only ever built as `securityQuestion<N>Text`, so a digit run is always present.
    return (selectName.match(/\d+/g) as RegExpMatchArray)[0];
}

const GET_QUESTIONS_BY_USERNAME = graphql(`
    query GetQuestionsByUsername($username: String!) {
        getUserSecurityQuestions(username: $username) {
            securityQuestion1Id
            securityQuestion1Text
            securityQuestion2Id
            securityQuestion2Text
            securityQuestion3Id
            securityQuestion3Text
        }
    }
`);

const VALIDATE_SECURITY_ANSWER = graphql(`
    query ValidateSecurityAnswer($username: String, $questionId: String!, $answer: String!, $userId: String) {
        validateUserSecurityAnswer(username: $username, questionId: $questionId, answer: $answer, userId: $userId) {
            userId
            username
            temppass
            activationCode
        }
    }
`);
